import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import CategorySelect from '../CategorySelect';
import DateRangeCalendar from '../DateRangeCalendar';
import usePrefersReducedMotion from '../../hooks/usePrefersReducedMotion';
import useMediaQuery from '../../utils/useMediaQuery';
import useTopBarHeight from '../../hooks/useTopBarHeight';
import { RING_STOPS, EASE, fillForDark, inkForDark, gutter } from './wallTokens';
import { LocationTabButton } from '../chrome/LocationControl';
import { isLocationPanelOpen, onLocationPanel, toggleLocationPanel } from '../../services/userLocation';
import useUserLocation from '../../hooks/useUserLocation';
import '../chrome/topHeader.css';

/**
 * FILTERS — the tab that hangs below the black top header and the drawer it drops
 * (events-wall handoff §2; the tab is the top-header handoff §5, Sept 25 2026). It replaces the filter row that used to live in ALL EVENTS; the
 * state is `useWallFilters`, so the same selection narrows Featured and All Events.
 *
 * The wrapper is sticky and zero-height, directly under the top bar: the tab hangs from it,
 * centred, and the drawer slides down out of it over the page — it never pushes content. There is
 * no spacer: the white PORTALS rail starts flush under the bar and the tab hangs over its top
 * padding (28px phone / 32px desktop, StoriesRail), so no sky shows between the two. While search
 * is open the whole thing fades
 * out (`.th-filters`, keyed off the TopBar's `data-pr-search-open`).
 *
 * Location used to be the "Showing parties near:" strip. That strip is gone and narrowing lives
 * in Cities, so the Cities panel carries the NEAR line (change / clear / set) as well.
 *
 * Oct 14 2026 (top-header + location handoff §3): the drawer is black now, the same Portal
 * surface as the search panel — only the colours changed, every bit of the behaviour is as it
 * was, including the trace that runs along the header's bottom edge and around this tab. Where
 * the header row has no space for the location pill (phones, and a desktop under 1100) the pill
 * rides in this tab instead, left of FILTERS behind a 1px rule, and the trace re-measures itself
 * around the wider tab as it always did.
 */
const ICO = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'square', strokeLinejoin: 'miter', style: { flexShrink: 0 } };
const ICONS = {
  when: (s) => (<svg width={s} height={s} {...ICO}><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4M8 2v4M3 10h18" /></svg>),
  type: (s) => (<svg width={s} height={s} {...ICO}><path d="M4 5h16a1 1 0 0 1 1 1v3a2.5 2.5 0 0 0 0 5v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2.5 2.5 0 0 0 0-5V6a1 1 0 0 1 1-1z" /><path d="M9 5v14" /></svg>),
  category: (s) => (<svg width={s} height={s} {...ICO}><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><circle cx="7" cy="7" r="1.2" /></svg>),
  cities: (s) => (<svg width={s} height={s} {...ICO}><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="2.5" /></svg>),
};
const Chevron = ({ open, reduced }) => (
  <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="square" strokeLinejoin="miter"
    style={{ transform: open ? 'rotate(180deg)' : 'none', transition: reduced ? 'none' : 'transform .25s', flexShrink: 0 }}>
    <path d="M2 4l4 4 4-4" />
  </svg>
);

const KEYS = ['when', 'type', 'category', 'cities'];
const TYPES = [['all', 'All'], ['guaranteed', 'Guaranteed'], ['party_starter', 'Party Starter']];

/** The tab's height; the open drawer (and the rail's top padding) clear it. */
const TAB_H = 26;

/**
 * The FILTERS tab's border trace (top-header handoff §5): one path along the header's bottom
 * edge that turns down into the tab through 8px fillets, round its 14px bottom corners and back
 * up. Stroke 1 flows the RING along it (4s); stroke 2 is a white glint travelling left to right
 * (3.2s). The fillet corners are filled black so the tab reads as part of the header.
 */
function FiltersTrace({ width: w, tab, reduced }) {
  const id = `prft${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  if (!w || !tab) return null;
  const { w: pw, h: ph } = tab;
  const L = w / 2 - pw / 2 + 1;
  const R = w / 2 + pw / 2 - 1;
  const B = ph + 1;
  const r = 14;
  const f = 8;
  const d = `M0 1H${L - f}A${f} ${f} 0 0 1 ${L} ${1 + f}V${B - r}A${r} ${r} 0 0 0 ${L + r} ${B}H${R - r}A${r} ${r} 0 0 0 ${R} ${B - r}V${1 + f}A${f} ${f} 0 0 1 ${R + f} 1H${w}`;
  const fillet = `M${L - f} 0V1A${f} ${f} 0 0 1 ${L} ${1 + f}V0Z M${R} 0V${1 + f}A${f} ${f} 0 0 1 ${R + f} 1V0Z`;
  const n = RING_STOPS.length;
  return (
    <svg aria-hidden="true" width="100%" height="32" data-testid="filters-trace"
      style={{ position: 'absolute', zIndex: 3, left: 0, top: -2, overflow: 'visible', pointerEvents: 'none' }}>
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="480" y2="0" spreadMethod="repeat">
          {[...RING_STOPS, RING_STOPS[0]].map((c, i) => <stop key={i} offset={i / n} stopColor={c} />)}
          {!reduced && <animateTransform attributeName="gradientTransform" type="translate" from="0 0" to="480 0" dur="4s" repeatCount="indefinite" />}
        </linearGradient>
      </defs>
      <path d={fillet} fill="#000" />
      <path d={d} fill="none" stroke={`url(#${id})`} strokeWidth="2" strokeLinejoin="round" />
      {!reduced && (
        <path d={d} fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" pathLength="100" strokeDasharray="6 94" strokeDashoffset="100" opacity=".85">
          <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3.2s" repeatCount="indefinite" />
        </path>
      )}
    </svg>
  );
}

function Chip({ on, onClick, children, ariaPressed = true }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={ariaPressed ? on : undefined}
      className="whitespace-nowrap rounded-full font-mono font-bold uppercase transition-colors duration-200"
      style={{ padding: '7px 14px', fontSize: 10, letterSpacing: '0.06em', border: '2px solid transparent', background: fillForDark(on ? 'on' : 'off'), color: inkForDark(on ? 'on' : 'off') }}
    >
      {children}
    </button>
  );
}

export default function FilterDrawer({ filters, location = null, locationLabel = '', locationStatus = 'idle', onChangeLocation, onClearLocation }) {
  const phone = useMediaQuery('(max-width: 639px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const pillRoom = useMediaQuery('(min-width: 1100px)');
  // The pill is in the tab exactly where the header row cannot hold it.
  const locInTab = phone || (isDesktop && !pillRoom);
  const browse = useUserLocation();
  const [locOpen, setLocOpen] = useState(isLocationPanelOpen);
  useEffect(() => onLocationPanel(setLocOpen), []);
  // Opening the location panel closes this drawer — one panel hangs off the bar at a time.
  useEffect(() => { if (locOpen) { setDrawerOpen(false); setOpenFilter(null); } }, [locOpen]);
  const changeLocation = onChangeLocation || (() => toggleLocationPanel(true));
  const reduced = usePrefersReducedMotion();
  const navH = useTopBarHeight();
  const pad = gutter(phone);
  const barRef = useRef(null);
  const tabRef = useRef(null);
  // The trace is drawn in the header's pixels: its width and the tab's box, re-measured on resize.
  const [barW, setBarW] = useState(0);
  const [tabBox, setTabBox] = useState(null);
  useLayoutEffect(() => {
    const bar = barRef.current;
    const tabEl = tabRef.current;
    if (!bar || !tabEl) return undefined;
    const measure = () => {
      setBarW(Math.round(bar.getBoundingClientRect().width) || 0);
      setTabBox((prev) => {
        const next = { w: tabEl.offsetWidth, h: tabEl.offsetHeight };
        return prev && prev.w === next.w && prev.h === next.h ? prev : next;
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    ro.observe(tabEl);
    return () => ro.disconnect();
  }, []);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openFilter, setOpenFilter] = useState(null);
  // The last opened filter stays rendered while its panel collapses, so it folds away instead
  // of emptying and then folding.
  const lastRef = useRef(null);
  if (openFilter) lastRef.current = openFilter;
  const shown = openFilter || lastRef.current;

  const f = filters;

  // Outside click or Esc closes the drawer and any open filter.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onDown = (e) => { if (barRef.current && !barRef.current.contains(e.target)) { setDrawerOpen(false); setOpenFilter(null); } };
    const onKey = (e) => { if (e.key === 'Escape') { setDrawerOpen(false); setOpenFilter(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [drawerOpen]);

  const toggleDrawer = () => {
    setDrawerOpen((o) => !o);
    if (drawerOpen) setOpenFilter(null);
  };
  const toggle = (k) => setOpenFilter((cur) => (cur === k ? null : k));
  const stateOf = (k) => (f.groups[k] ? 'on' : openFilter === k ? 'open' : 'off');
  const slide = (ms) => (reduced ? 'none' : `transform ${ms}ms ${EASE}`);

  const pill = (k) => {
    const st = stateOf(k);
    const common = {
      key: k,
      type: 'button',
      onClick: () => toggle(k),
      'aria-expanded': openFilter === k,
      'data-testid': `filter-${k}`,
      'data-state': st,
      className: 'flex items-center rounded-full font-mono transition-colors duration-200',
    };
    const colors = { border: '2px solid transparent', background: fillForDark(st), color: inkForDark(st) };
    if (phone) {
      return (
        <button {...common} style={{ ...colors, minWidth: 0, justifyContent: 'center', gap: 5, height: 36, padding: '0 6px', fontSize: 11, fontWeight: 600, letterSpacing: '0.02em' }}>
          {ICONS[k](13)}
          <span className="min-w-0 truncate">{f.labels[k]}</span>
        </button>
      );
    }
    return (
      <button {...common} style={{ ...colors, flexShrink: 0, gap: 8, height: 40, padding: '0 16px', fontSize: 13, whiteSpace: 'nowrap' }}>
        {ICONS[k](15)}
        <span>{f.labels[k]}</span>
        <Chevron open={openFilter === k} reduced={reduced} />
      </button>
    );
  };

  // NEAR {CITY} · CHANGE — the city is the one the header pill shows, and CHANGE opens that
  // same panel rather than a second picker.
  const nearCity = locationLabel || browse.city;
  const nearLine = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] uppercase tracking-[0.1em]">
      <span style={{ color: '#8A8A8A' }}>Near</span>
      {locationStatus === 'asking' ? (
        <span style={{ color: '#8A8A8A' }}>Locating you…</span>
      ) : (
        <>
          <span className="font-bold text-white">{nearCity}</span>
          <button type="button" onClick={changeLocation} data-testid="filters-change-location" className="text-white underline hover:no-underline">Change</button>
          {location && onClearLocation && (
            <button type="button" onClick={onClearLocation} className="hover:text-white" style={{ color: '#8A8A8A' }} title="Show events from anywhere">Clear</button>
          )}
        </>
      )}
    </div>
  );

  const panel = (
    <div className="flex flex-col gap-2.5" style={{ padding: phone ? '12px 0 14px' : '14px 0 18px' }}>
      {shown === 'when' && (
        <>
          <div className="flex gap-1">
            {f.week.map((d, i) => {
              const on = f.selectedDays.includes(d.iso);
              return (
                <button
                  key={d.iso}
                  type="button"
                  onClick={() => f.toggleDay(d.iso)}
                  aria-pressed={on}
                  title={i === 0 ? 'Today' : d.iso}
                  className="flex min-w-0 flex-1 flex-col items-center font-mono leading-none transition-colors duration-200"
                  style={{ gap: 3, padding: '9px 0', border: '2px solid transparent', borderRadius: 14, background: fillForDark(on ? 'on' : 'off'), color: inkForDark(on ? 'on' : 'off') }}
                >
                  <span style={{ fontSize: 9, fontWeight: 700 }}>{d.dow}</span>
                  <span style={{ fontSize: 13, fontWeight: 700 }}>{d.dayNum}</span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <Chip on={f.todayActive} onClick={f.setToday}>Today</Chip>
            <Chip on={f.weekendActive} onClick={f.setWeekend}>Weekend</Chip>
          </div>
          <div className="pt-1">
            <p className="mb-2 font-mono text-[9px] font-bold uppercase tracking-[0.16em]" style={{ color: '#8A8A8A' }}>Or pick a date / range</p>
            <DateRangeCalendar selected={f.selectedDays} onPickRange={f.pickRange} minIso={f.todayIso} />
          </div>
        </>
      )}
      {shown === 'type' && (
        <div className="flex flex-wrap gap-2">
          {TYPES.map(([k, l]) => (
            <Chip key={k} on={f.fundingFilter === k} onClick={() => { f.setFundingFilter(k); setOpenFilter(null); }}>{l}</Chip>
          ))}
        </div>
      )}
      {shown === 'category' && (
        <CategorySelect
          variant="pills"
          autoFocus={openFilter === 'category'}
          selected={f.activeCats}
          onToggle={f.toggleCategory}
          onClear={() => f.toggleCategory('')}
        />
      )}
      {shown === 'cities' && (
        <>
          {nearLine}
          {f.cities.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {f.cities.map((c) => (
                <Chip key={c} on={f.selectedCities.some((x) => x.toLowerCase() === c.toLowerCase())} onClick={() => f.toggleCity(c)}>{c}</Chip>
              ))}
            </div>
          ) : (
            <p className="font-mono text-[11px] uppercase tracking-[0.1em]" style={{ color: '#8A8A8A' }}>No cities to narrow to yet.</p>
          )}
        </>
      )}
    </div>
  );

  return (
    <div ref={barRef} className="th-filters sticky z-[41]" style={{ top: navH, height: 0 }} data-testid="filter-bar">
      {/* The drawer. overflow-hidden clips it while it's tucked up under the bar. */}
      <div className="absolute inset-x-0 top-0 overflow-hidden" style={{ paddingBottom: 4, pointerEvents: drawerOpen ? 'auto' : 'none' }} aria-hidden={!drawerOpen}>
        <div
          className="text-white"
          style={{ transform: drawerOpen ? 'translateY(0)' : 'translateY(-100%)', transition: slide(320), background: '#000', borderRadius: '0 0 28px 28px', paddingTop: TAB_H + 8 }}
          data-testid="filter-drawer"
          {...(!drawerOpen ? { inert: true } : {})}
        >
          {phone ? (
            <div className="grid gap-1.5" style={{ gridTemplateColumns: f.anyActive ? 'repeat(4,minmax(0,1fr)) 36px' : 'repeat(4,minmax(0,1fr))', padding: `8px ${pad}px` }}>
              {KEYS.map(pill)}
              {f.anyActive && (
                <button type="button" onClick={() => { f.clearAll(); setOpenFilter(null); }} aria-label="Clear all filters" className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 999, background: '#1F1F1F', color: '#fff' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="square"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              )}
            </div>
          ) : (
            <div className="mx-auto flex max-w-[1280px] items-center gap-3" style={{ padding: `12px ${pad}px` }}>
              <div className="flex min-w-0 flex-1 flex-wrap gap-2">{KEYS.map(pill)}</div>
              {f.anyActive && (
                <button type="button" onClick={() => { f.clearAll(); setOpenFilter(null); }} className="flex-shrink-0 font-mono text-[12px] tracking-[0.1em] text-white underline hover:no-underline">
                  CLEAR ALL
                </button>
              )}
            </div>
          )}

          {/* Options: height by grid-template-rows 0fr → 1fr, so it animates to whatever the
              content needs. */}
          <div
            style={{
              display: 'grid',
              gridTemplateRows: openFilter ? '1fr' : '0fr',
              transition: reduced ? 'none' : `grid-template-rows 280ms ${EASE}`,
              pointerEvents: openFilter ? 'auto' : 'none',
            }}
          >
            <div className="min-h-0 overflow-hidden" style={{ opacity: openFilter ? 1 : 0, transition: reduced ? 'none' : 'opacity .2s linear' }}>
              <div className="mx-auto max-w-[1280px]" style={{ padding: `0 ${pad}px` }}>
                {shown && panel}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FILTERS — a tab hanging fully below the bar, joined to it (top-header handoff §5):
          black, bottom corners rounded, centred at every width. Its border is traced by the SVG
          above it, which also runs the full width of the header's bottom edge. */}
      {/* The tab. With the location button in it, it is one shell holding two buttons divided by
          a hairline — the trace is measured from the shell, so it simply draws wider. */}
      <div
        ref={tabRef}
        data-testid="filters-tab-shell"
        className="absolute flex items-center bg-black font-mono text-white"
        style={{ left: '50%', top: 0, transform: 'translateX(-50%)', zIndex: 2, height: TAB_H, padding: '0 0 3px', boxSizing: 'border-box', borderRadius: '0 0 14px 14px', fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', whiteSpace: 'nowrap' }}
      >
        {locInTab && <LocationTabButton city={browse.city} open={locOpen} onToggle={() => toggleLocationPanel()} />}
        <button
          type="button"
          onClick={toggleDrawer}
          aria-expanded={drawerOpen}
          aria-label={f.activeCount ? `Filters, ${f.activeCount} on` : 'Filters'}
          data-testid="filters-tab"
          className="flex items-center font-mono"
          style={{ height: '100%', padding: locInTab ? '0 14px 0 12px' : '0 16px', border: 0, background: 'none', color: '#fff', gap: 7, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', cursor: 'pointer' }}
        >
          {f.activeCount ? `FILTERS · ${f.activeCount}` : 'FILTERS'}
          <Chevron open={drawerOpen} reduced={reduced} />
        </button>
      </div>
      <FiltersTrace width={barW} tab={tabBox} reduced={reduced} />
    </div>
  );
}
